import { z } from "zod";
import { readStoredClinicalPhase } from "./clinical-phase";
import type {
  BloodTestStatus,
  MedicalEvidenceTaskKey,
  MedicalEvidenceTaskStatus,
} from "./medical-evidence";
import { PRESCRIPTION_ISSUED_AWAITING_SUBSCRIPTION_LABEL } from "./treatment-start";
import { subscriptionPlanSchema } from "./subscription-plan";

// TES-944 (TES-941 §PATIENTHEADER) — the doctor's patient header shows the
// clinical phase and the subscription as two separate fields. Emelie:
//   "Headern ska skilja på klinisk fas och abonnemangsstatus."
// The old header had one "Status" that printed the treatment state when a
// treatment existed and the journey state otherwise, so "Aktiv" could mean
// either a live prescription or a paid subscription.
//
// The derivations live here rather than in the component because apps/admin
// has no test runner; this package does.

// ---- Klinisk fas ------------------------------------------------------------

// Emelie's seven values, in her order. The keys are ours; the labels are hers,
// verbatim.
export const CLINICAL_STAGES = [
  "assessment",
  "awaiting_completion",
  "awaiting_blood_test",
  // TES-1182 — see deriveClinicalStage, rule 8.
  "awaiting_decision",
  "approved",
  // TES-1034 S5 — see deriveClinicalStage, rule 7.
  "approved_awaiting_subscription",
  "active_treatment",
  "weight_stabilisation",
  "ended",
] as const;
export const clinicalStageSchema = z.enum(CLINICAL_STAGES);
export type ClinicalStage = z.infer<typeof clinicalStageSchema>;

export const CLINICAL_STAGE_LABEL: Record<ClinicalStage, string> = {
  assessment: "Ansökan/medicinsk bedömning",
  awaiting_completion: "Inväntar komplettering",
  awaiting_blood_test: "Inväntar blodprov",
  // TES-1182 (TES-1180 point 7) — Emelie's example, verbatim:
  // "Patienten bör stå kvar som exempelvis Inväntar medicinsk bedömning tills slutligt beslut har fattats och publicerats."
  awaiting_decision: "Inväntar medicinsk bedömning",
  approved: "Godkänd för behandling",
  // TES-1141 — was "Godkänd – inväntar abonnemang" (transfer only).
  // TES-1150 — only once the doctor has registered the prescription; before
  // that the stage is "Godkänd för behandling" (his step is next).
  approved_awaiting_subscription:
    PRESCRIPTION_ISSUED_AWAITING_SUBSCRIPTION_LABEL,
  active_treatment: "Aktiv behandling",
  weight_stabilisation: "Viktstabilisering",
  ended: "Avslutad",
};

export interface ClinicalStageInput {
  /** Patient.journeyState (JOURNEY_STATES; a free string on the wire). */
  journeyState: string;
  /** Treatment.state, or null when no Treatment row exists. */
  treatmentState: string | null;
  /** Treatment.clinicalPhase as STORED (read through readStoredClinicalPhase). */
  storedClinicalPhase: string | null;
  /** The open-or-latest PrescriptionTransferReview's status, if any. */
  transferReviewStatus: string | null;
  /** The CP-06 checklist (medicalEvidenceState): the open completion case. */
  evidence: {
    active: boolean;
    tasks: ReadonlyArray<{
      key: MedicalEvidenceTaskKey;
      status: MedicalEvidenceTaskStatus;
    }>;
  };
  /**
   * The case's blood-test status from TES-942's deriveBloodTestStatus (the one
   * status admin and the app both read), or null when the case has no blood
   * test.
   */
  bloodTestStatus: BloodTestStatus | null;
  /**
   * TES-1034 S5 — the patient's FIRST prescription cycle is still
   * pending_assessment. For an approved transfer that means the approval is
   * recorded but not yet activated: the transfer's cycle is activated by her
   * subscription (transfer-activation.ts), never by the doctor. Optional so
   * an older caller reads as before.
   */
  firstCyclePending?: boolean;
  /**
   * TES-1141 — the subscription is entitled (billing-status.ts
   * ENTITLED_STATUSES: trialing or active). When given, rule 7 reads it for
   * both journeys; when absent (an older caller), only an approved transfer
   * with a pending first cycle reads as awaiting the subscription.
   */
  subscriptionEntitled?: boolean;
  /**
   * TES-1150 — the doctor has registered the prescription on that pending
   * first cycle. When given, rule 7 reads "awaiting the subscription" only
   * after it; when absent (an older caller), as before.
   */
  prescriptionRegistered?: boolean;
}

// Blood-test statuses in which the patient's side, or the lab's, is still
// outstanding. From result_registered on, the doctor has what he needs.
const BLOOD_TEST_OUTSTANDING: ReadonlySet<BloodTestStatus> = new Set([
  "awaiting_payment",
  "awaiting_sample",
  "sample_reported",
]);

// Journey states in which a treatment has been started (the overlay `paused`
// is only enterable from them in practice).
const TREATMENT_ERA_JOURNEY = new Set([
  "treatment_started",
  "active_treatment",
  "maintenance",
  "paused",
]);
// Treatment states that mean a prescription is (or was until a pause) live.
const LIVE_TREATMENT = new Set(["active", "between_cycles", "paused"]);

/**
 * The header's "Klinisk fas", derived from what is stored. Never stored itself.
 *
 * First matching row wins (condition → label):
 *
 *   1. journeyState = ended, or treatment ended while the journey is in the
 *      treatment era (a returning patient's new application falls through)
 *      → "Avslutad"
 *   2. treatment live (active/between_cycles/paused) or journey in the
 *      treatment era → "Viktstabilisering" when the stored phase is
 *      weight_stabilisation (or none is stored and journey = maintenance),
 *      otherwise "Aktiv behandling"
 *   3. CP-06 checklist active and the case's blood test (TES-942
 *      deriveBloodTestStatus) is awaiting_payment, awaiting_sample or
 *      sample_reported → "Inväntar blodprov"
 *   4. CP-06 checklist active with any other task missing
 *      → "Inväntar komplettering"
 *   5. no active checklist, and transfer review or journey = labs_required
 *      → "Inväntar blodprov"
 *   6. no active checklist and transfer review = completion_requested (an
 *      untargeted request opens no checklist case) → "Inväntar komplettering"
 *   7. transfer review = approved, or a stored clinical phase (written only
 *      by a positive decision) → "Godkänd för behandling"; but a positive
 *      decision (an approved
 *      transfer, or a stored phase) whose first cycle is still
 *      pending_assessment while the subscription is not entitled
 *      → "Inväntar aktiverat abonnemang" (TES-1034 S5, Emelie point 7: the
 *      decision, the phase and the cycle must tell the same story; TES-1141:
 *      for a new treatment too, since the prescription must not be issued
 *      before the subscription is active)
 *   8. journey = treatment_decision_ready without a positive decision
 *      → "Inväntar medicinsk bedömning" (TES-1182)
 *   9. anything else → "Ansökan/medicinsk bedömning"
 *
 * Why treatment_decision_ready is not "Godkänd för behandling" (TES-1182,
 * TES-1180 point 7): the journey reaches it when the doctor documents a
 * meeting that proceeds, approves a lab order or sends lab feedback — all
 * before a decision is published. Emelie: "En planerad läkemedelsbehandling
 * får inte i sig ändra patientens kliniska fas till godkänd." Only what a
 * published positive decision writes counts.
 *
 * Why the treatment era wins over an open checklist: since TES-736 a doctor
 * may approve with follow-up remaining, and the follow-up lives on in CP-06.
 * She is then in treatment with an open task, not back in the application.
 *
 * Why an active checklist overrides journey = labs_required: the journey is
 * monotonic and stays on labs_required after uploaded earlier results or a
 * self-reported sample. The checklist says what is actually still missing.
 */
export function deriveClinicalStage(input: ClinicalStageInput): ClinicalStage {
  const { journeyState, treatmentState, evidence } = input;
  const inTreatmentEra = TREATMENT_ERA_JOURNEY.has(journeyState);

  if (journeyState === "ended") return "ended";
  if (treatmentState === "ended" && inTreatmentEra) return "ended";

  if (
    (treatmentState && LIVE_TREATMENT.has(treatmentState)) ||
    inTreatmentEra
  ) {
    const phase = readStoredClinicalPhase(input.storedClinicalPhase);
    if (phase === "weight_stabilisation") return "weight_stabilisation";
    if (phase === null && journeyState === "maintenance")
      return "weight_stabilisation";
    return "active_treatment";
  }

  if (evidence.active) {
    if (
      input.bloodTestStatus !== null &&
      BLOOD_TEST_OUTSTANDING.has(input.bloodTestStatus)
    )
      return "awaiting_blood_test";
    if (evidence.tasks.some((t) => t.status === "missing"))
      return "awaiting_completion";
  } else {
    if (
      input.transferReviewStatus === "labs_required" ||
      journeyState === "labs_required"
    )
      return "awaiting_blood_test";
    if (input.transferReviewStatus === "completion_requested")
      return "awaiting_completion";
  }

  const decided =
    input.transferReviewStatus === "approved" ||
    readStoredClinicalPhase(input.storedClinicalPhase) !== null;
  if (decided && input.firstCyclePending) {
    const awaiting =
      input.subscriptionEntitled === undefined
        ? input.transferReviewStatus === "approved"
        : !input.subscriptionEntitled;
    if (awaiting && input.prescriptionRegistered !== false) {
      return "approved_awaiting_subscription";
    }
  }
  if (decided) return "approved";
  if (journeyState === "treatment_decision_ready") return "awaiting_decision";

  return "assessment";
}

// ---- Abonnemang ------------------------------------------------------------

// Emelie's three values, and only those.
export const SUBSCRIPTION_STANDINGS = [
  "inactive",
  "active_with_coach",
  "active_without_coach",
] as const;
export const subscriptionStandingSchema = z.enum(SUBSCRIPTION_STANDINGS);
export type SubscriptionStanding = z.infer<typeof subscriptionStandingSchema>;

export const SUBSCRIPTION_STANDING_LABEL: Record<SubscriptionStanding, string> =
  {
    inactive: "Inte aktiverat",
    active_with_coach: "Aktivt – med coach",
    active_without_coach: "Aktivt – utan coach",
  };

/**
 * Both inputs are the server's answers, passed in rather than restated here so
 * the header cannot disagree with the surfaces that act on them:
 *   - `entitled`: billing-status.ts ENTITLED_STATUSES (trialing and active);
 *   - `coachAccess`: coachAccessForPatient (billing/coach-access.ts, TES-896),
 *     which reads coachAccessFor(plan) and treats a NULL plan as coach active
 *     once a subscription has existed (TES-1121: closed before the first
 *     one). It is read here only when she is entitled.
 */
export function subscriptionStanding(input: {
  entitled: boolean;
  coachAccess: "active" | "inactive";
}): SubscriptionStanding {
  if (!input.entitled) return "inactive";
  return input.coachAccess === "active"
    ? "active_with_coach"
    : "active_without_coach";
}

export const clinicianSubscriptionSchema = z.object({
  // Stripe's status verbatim ('none' when no row exists).
  status: z.string(),
  plan: subscriptionPlanSchema.nullable(),
  standing: subscriptionStandingSchema,
});
export type ClinicianSubscription = z.infer<typeof clinicianSubscriptionSchema>;

// ---- Blodtryck och puls ------------------------------------------------------

// The newest measurement session that still stands (not superseded, TES-863)
// and carries a blood pressure or a pulse. Either half may be missing: the
// "Dina värden" entry makes pulse optional.
export const bloodPressurePulseReadingSchema = z.object({
  systolic: z.number().int().nullable(),
  diastolic: z.number().int().nullable(),
  pulse: z.number().nullable(),
  measuredAt: z.string(),
});
export type BloodPressurePulseReading = z.infer<
  typeof bloodPressurePulseReadingSchema
>;

/**
 * "128/82 · puls 68 · mätt 24 sep." — or null when there is nothing to show.
 * Emelie: "Saknade blodtrycks- eller pulsvärden ska inte ligga som permanenta varningar i headern."
 * so a missing reading renders as nothing, not as a warning.
 */
export function formatBloodPressurePulse(
  reading: BloodPressurePulseReading | null | undefined,
): string | null {
  if (!reading) return null;
  const parts: string[] = [];
  if (reading.systolic != null && reading.diastolic != null)
    parts.push(`${reading.systolic}/${reading.diastolic}`);
  if (reading.pulse != null) parts.push(`puls ${Math.round(reading.pulse)}`);
  if (parts.length === 0) return null;
  const date = new Date(reading.measuredAt).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    timeZone: "Europe/Stockholm",
  });
  parts.push(`mätt ${date}`);
  return parts.join(" · ");
}
